'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { BlogPost } from '@/lib/blog';

interface ArticleContentProps {
  post: BlogPost;
}

type Heading = { id: string; text: string; level: 2 | 3 };

/**
 * Article body — F-500 2026 editorial layout.
 *
 * Reading-progress bar on top, calm editorial header (no card-in-card,
 * no avatar bubbles), sticky table-of-contents in the right rail on
 * desktop, author-box + newsletter-CTA + topic-tags after the body.
 *
 * TOC + active-section tracking: we scan the rendered body for h2/h3
 * tags, slug their text into IDs, then watch with IntersectionObserver
 * which section is currently in view.
 */
export function ArticleContent({ post }: ArticleContentProps) {
  const t = useTranslations('blog');
  const [progress, setProgress] = useState(0);
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Reading-progress bar
  useEffect(() => {
    const onScroll = () => {
      const top = document.documentElement.scrollTop;
      const max =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setProgress(max > 0 ? (top / max) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Build TOC from rendered body. Assigns IDs to h2/h3 in place so the
  // anchor links work, then watches them with IntersectionObserver.
  useEffect(() => {
    if (!bodyRef.current) return;
    const slug = (s: string) =>
      s
        .toLowerCase()
        .replace(/[^a-z0-9äöüß\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '');
    const nodes = bodyRef.current.querySelectorAll('h2, h3');
    const list: Heading[] = [];
    const used = new Set<string>();
    nodes.forEach((n) => {
      const text = n.textContent?.trim() || '';
      if (!text) return;
      let id = slug(text);
      let i = 2;
      while (used.has(id)) id = `${slug(text)}-${i++}`;
      used.add(id);
      n.id = id;
      list.push({ id, text, level: n.tagName === 'H2' ? 2 : 3 });
    });
    setHeadings(list);

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0.1 },
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [post.content]);

  const dateLabel = new Date(post.date).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <>
      {/* Reading-progress bar */}
      <div
        className="fixed left-0 right-0 top-0 z-50 h-1 bg-gray-100"
        aria-hidden="true"
      >
        <div
          className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      <article className="mx-auto max-w-7xl">
        {/* Editorial header — calm, no card-in-card */}
        <header className="mx-auto max-w-3xl">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
            <span className="text-blue-600">{post.category}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={post.date}>{dateLabel}</time>
            <span aria-hidden="true">·</span>
            <span>{post.readingTime}</span>
          </div>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
            {post.title}
          </h1>
          <p className="mt-6 text-xl leading-relaxed text-gray-600">
            {post.description}
          </p>
        </header>

        {/* Cover */}
        {post.coverImage && (
          <div className="mx-auto mt-12 max-w-5xl overflow-hidden rounded-2xl border border-gray-200 bg-gray-100">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={post.coverImage}
              alt=""
              className="aspect-[16/9] w-full object-cover"
            />
          </div>
        )}

        {/* Body + Sticky TOC sidebar */}
        <div className="mx-auto mt-16 grid max-w-6xl gap-12 lg:grid-cols-[1fr_240px]">
          <div
            ref={bodyRef}
            className="prose prose-lg max-w-none prose-gray
              prose-headings:font-semibold prose-headings:tracking-tight prose-headings:scroll-mt-24
              prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-5 prose-h2:text-gray-900
              prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-3 prose-h3:text-gray-900
              prose-p:leading-relaxed prose-p:text-gray-700
              prose-a:text-blue-600 prose-a:no-underline prose-a:font-medium hover:prose-a:underline
              prose-strong:text-gray-900
              prose-blockquote:border-l-2 prose-blockquote:border-blue-600 prose-blockquote:bg-blue-50/40 prose-blockquote:not-italic prose-blockquote:font-normal prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:px-5
              prose-code:bg-gray-100 prose-code:text-blue-700 prose-code:rounded prose-code:px-1.5 prose-code:py-0.5 prose-code:before:content-none prose-code:after:content-none
              prose-pre:bg-gray-950 prose-pre:rounded-xl prose-pre:text-gray-100
              prose-img:rounded-xl prose-img:border prose-img:border-gray-200
              prose-table:text-sm
              prose-th:bg-gray-50 prose-th:font-semibold prose-th:text-gray-900
              prose-hr:border-gray-200
              prose-li:marker:text-gray-400
            "
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* TOC sidebar — sticky, desktop only */}
          {headings.length > 1 && (
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  {t('toc')}
                </p>
                <nav className="mt-4 border-l border-gray-200">
                  <ul className="space-y-1.5">
                    {headings.map((h) => (
                      <li key={h.id}>
                        <a
                          href={`#${h.id}`}
                          className={`block border-l-2 py-1 text-sm transition-colors ${
                            h.level === 3 ? 'pl-7' : 'pl-4'
                          } ${
                            activeId === h.id
                              ? 'border-blue-600 text-blue-700 font-medium'
                              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900'
                          }`}
                          style={{ marginLeft: '-1px' }}
                        >
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>
          )}
        </div>

        {/* Inline Newsletter / Trial-CTA — subtle, only one per article */}
        <aside className="mx-auto mt-20 max-w-3xl rounded-2xl border border-blue-100 bg-blue-50/40 p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
            EazyBooking
          </p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-gray-900">
            Online-Termine ohne No-Shows — 14 Tage gratis testen
          </h3>
          <p className="mt-3 text-base leading-relaxed text-gray-600">
            Apple-, Google- und Outlook-Kalender-Sync, Anzahlung pro Service,
            automatische Erinnerungen, eigene Buchungs-Domain. Ohne Kreditkarte
            starten.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              href="/register"
              className="inline-flex h-11 items-center justify-center rounded-full bg-gray-950 px-6 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
            >
              Jetzt starten
            </Link>
            <Link
              href="/"
              className="inline-flex h-11 items-center justify-center rounded-full border border-gray-300 bg-white px-6 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-900"
            >
              Mehr erfahren
            </Link>
          </div>
        </aside>

        {/* Author-Box */}
        <section className="mx-auto mt-16 flex max-w-3xl flex-col gap-5 rounded-2xl border border-gray-200 bg-white p-7 sm:flex-row sm:items-center">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-lg font-semibold text-blue-700">
            {post.author
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              {t('authorLabel')}
            </p>
            <p className="mt-1 text-base font-semibold text-gray-900">
              {post.author}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              Wir bauen EazyBooking — eine Online-Terminbuchung für
              Service-Businesses in der DACH-Region. Hosted in Frankfurt,
              DSGVO-konform, ohne Provision.
            </p>
          </div>
        </section>

        {/* Topic-Tags */}
        {post.keywords && post.keywords.length > 0 && (
          <section className="mx-auto mt-12 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              {t('relatedTopics')}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {post.keywords.map((keyword) => (
                <span
                  key={keyword}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
